import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { TalentHappyPathDataService } from '../../../shared/service/talent-happy-path-data-service';
import { hasAlreadyRecorded, VideoInterviewApiService, VideoInterviewResponse } from '../service/video-interview-api.service';
import { VideoInterviewFlowService } from '../service/video-interview-flow.service';

/** What the page is showing. */
export type VideoInterviewPageState = 'loading' | 'recording' | 'alreadyDone' | 'deferred';

/** The vendor's postMessage type meaning the talent reached the final screen. */
const INTERVIEW_FINISHED_EVENT = 'interview.finished';

/**
 * How long to wait before re-reading a deferred provision. Sized to the window a slow-but-successful
 * invite lands in: the service answers at its 4.5s budget while one vendor attempt is allowed 9s.
 * Longer retries are the pending-provision completer's job, not the talent's to wait for.
 */
const PENDING_RECHECK_MS = 5000;

/**
 * The video interview step: the vendor's recorder embedded in the funnel.
 *
 * Three rules from ADR-0004 (video-interview-api/adr). The step is optional, so every failure lands
 * on the deferred state rather than an error page. The "open in a new tab" escape hatch is
 * permanent, because camera capture in a cross-origin iframe is the most fragile thing here and the
 * vendor blocks some in-app browsers outright. And the vendor's `interview.finished` message is a
 * hint, never state — completion truth is the webhook pipeline, so this page never gates the funnel
 * on the status endpoint.
 */
@Component({
    selector: 'app-video-interview-page',
    imports: [
        MatButton,
        MatProgressSpinnerModule,
        TranslateModule
    ],
    templateUrl: './video-interview-page.component.html',
    styleUrl: './video-interview-page.component.scss'
})
export class VideoInterviewPageComponent implements OnInit, OnDestroy {

  state: VideoInterviewPageState = 'loading';

  /** The vendor page, trusted for the iframe. Angular blocks an unsanitized iframe src outright. */
  embedSrc: SafeResourceUrl | null = null;

  /** The same page as a plain link, for the escape hatch. */
  externalUrl: string | null = null;

  /**
   * Whether the talent opened the interview in a new tab, which is what reveals Continue. A new tab
   * is a top-level page that cannot post a message back to ours, so the talent telling us is the
   * only completion signal we get. Once true it stays true: they may return to this tab unfinished.
   */
  openedInNewTab = false;

  /** Present only in the recording state, so every use is optional. */
  @ViewChild('videoInterviewFrame') private frame?: ElementRef<HTMLIFrameElement>;

  private personId: number | null = null;
  private embedOrigin: string | null = null;
  private finished = false;
  private readonly subscriptions = new Subscription();

  private readonly onVendorMessage = (event: MessageEvent): void => {
    if (this.embedOrigin == null || event.origin !== this.embedOrigin) {
      return;
    }
    // Only the frame we loaded speaks for this interview. Origin alone would also admit a popup or
    // nested frame the vendor page opened, and a spurious finish ejects the talent mid-recording.
    const frameWindow = this.frame?.nativeElement.contentWindow;
    if (frameWindow == null || event.source !== frameWindow) {
      return;
    }
    // The message shape is undocumented, so accept an object with a type or a bare string. Only the
    // type is read; the payload is the talent's own recording.
    const data: unknown = event.data;
    const type = typeof data === 'object' && data !== null ? (data as { type?: unknown }).type : data;
    if (type !== INTERVIEW_FINISHED_EVENT) {
      // The only evidence of what the vendor actually posts.
      console.debug('Unhandled video interview frame message', type);
      return;
    }
    // The vendor may post more than once, and leaving issues a request and a navigation.
    if (this.finished) {
      return;
    }
    this.finished = true;
    this.clickContinue();
  };

  constructor(private videoInterviewApiService: VideoInterviewApiService,
              private videoInterviewFlowService: VideoInterviewFlowService,
              private dataService: TalentHappyPathDataService,
              private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.personId = this.dataService.talent?.personId;
    if (this.personId == null) {
      this.state = 'deferred';
      return;
    }
    // Provisioning happens here, not on the landing page: an invite creates a record at the vendor,
    // so a talent who clicks "I'll do this later" leaves nothing behind. The order comes off
    // postingInfo, which both entry paths populate; the data service's own orderId is VMS-only.
    this.subscriptions.add(
      this.videoInterviewApiService.provision(this.personId, this.dataService.postingInfo?.orderId)
        .subscribe({
          next: response => this.apply(response, true),
          error: error => {
            console.error('Error provisioning the video interview', error);
            this.state = 'deferred';
          }
        })
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onVendorMessage);
    this.subscriptions.unsubscribe();
    this.releaseFrame();
  }

  /**
   * Navigates the frame to a blank page while it is still in the document, which releases the media
   * players its document holds. Detaching alone leaves them to garbage collection, and Chrome caps
   * how many a renderer may hold before refusing more ("Blocked attempt to create a
   * WebMediaPlayer") — after which the vendor's camera preview fails with nothing thrown.
   *
   * Called from the exits because Angular removes the view's nodes before destroy hooks run, and
   * navigating an already-detached frame does nothing; `ngOnDestroy` covers exits without a button.
   */
  private releaseFrame(): void {
    if (this.frame != null) {
      this.frame.nativeElement.src = 'about:blank';
    }
  }

  /** Continue into the next funnel step, offered once the talent has left for a new tab. */
  clickContinue(): void {
    this.releaseFrame();
    this.videoInterviewFlowService.submitInterview();
  }

  clickIllDoThisLater(): void {
    this.releaseFrame();
    this.videoInterviewFlowService.doThisLater();
  }

  /** The escape hatch was used; the navigation itself is the anchor's, not ours. */
  clickOpenInNewTab(): void {
    this.openedInNewTab = true;
  }

  /**
   * Decides what to render from one answer.
   *
   * @param allowRecheck whether a deferred answer may be re-read once. False for the re-read
   *                     itself, so this cannot loop.
   */
  private apply(response: VideoInterviewResponse, allowRecheck: boolean): void {
    if (hasAlreadyRecorded(response)) {
      this.state = 'alreadyDone';
      return;
    }
    if (this.showRecorder(response)) {
      return;
    }
    // PENDING usually means the invite ran past the hot-path budget while still succeeding — the
    // service never cancels it, so it lands moments later. One re-read beats a deferred page for an
    // interview that is about to exist, and costs a database read rather than a vendor call.
    if (allowRecheck && response?.status === 'PENDING') {
      this.recheckOnce();
      return;
    }
    this.state = 'deferred';
  }

  /** Stays on the spinner and re-reads status once. The talent asked to start; they expect a wait. */
  private recheckOnce(): void {
    this.subscriptions.add(
      timer(PENDING_RECHECK_MS)
        .pipe(switchMap(() => this.videoInterviewApiService.readStatus(this.personId)))
        .subscribe({
          next: response => this.apply(response, false),
          error: error => {
            console.error('Error re-reading the video interview status', error);
            this.state = 'deferred';
          }
        })
    );
  }

  /**
   * Shows the recorder if this answer carries an embeddable interview. A live session with no page
   * to load is refused rather than rendering an empty frame, which is why this reports whether it
   * acted rather than assuming a live status implies a URL.
   *
   * @return true if the page is now recording
   */
  private showRecorder(response: VideoInterviewResponse): boolean {
    const embedUrl = response?.embedUrl;
    if ((response?.status !== 'INVITED' && response?.status !== 'IN_PROGRESS') || !embedUrl) {
      return false;
    }
    const origin = originOf(embedUrl);
    if (origin == null) {
      return false;
    }
    this.embedOrigin = origin;
    this.embedSrc = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    this.externalUrl = response.externalUrl ?? embedUrl;
    this.state = 'recording';
    window.addEventListener('message', this.onVendorMessage);
    return true;
  }
}

/**
 * https only, checked before `embedUrl` bypasses Angular's sanitizer. A `javascript:` or `data:`
 * URL has an opaque origin that serialises to the *string* `"null"`, so it survives an origin check
 * and then matches messages from any opaque-origin frame. An http frame inside our https page is
 * blocked as mixed content, which renders empty with nothing thrown.
 *
 * @return the origin to check vendor messages against, or null if this is not a URL we will embed.
 */
function originOf(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.origin;
  } catch {
    return null;
  }
}
